/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import ChipMenu from "../../../../../components/ChipMenu/ChipMenu";
import TestIds from "../../../../../testIds";
import { CATEGORY_DEFAULT_OPTION, SortingOptions } from "../../../../../util/consts";
import { ComponentFilters } from "../../../../products/components/ProductFilter";
import { DesktopCategoryMenu } from "../../../../products/components/ProductFilter/DesktopFilters";
import { FilterDropdown } from "../../../../products/components/ProductFilter/ProductFilterDropdownLayout";
import { getCategories } from "../../../../typesense/api";
import { sortOptions } from "../../../../typesense/Search";

const CategoriesDropDown: React.FC<{
  value: string | undefined;
  onCategoryChange: (value: string) => void;
}> = ({ onCategoryChange, value }) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [category, setCategory] = useState(value ?? CATEGORY_DEFAULT_OPTION);
  const [categories, setCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("");

  useEffect(() => {
    (async () => {
      const { categories } = await getCategories();
      setCategories(
        categories.map((e) => {
          return e.value;
        })
      );
      if (!sortOptions.includes(sortBy as SortingOptions)) {
        setSortBy(sortOptions[0]);
      }
    })();
  }, []);

  useEffect(() => {
    setCategoryOpen(false);
  }, [isMd]);

  useEffect(() => {
    onCategoryChange(category);
  }, [category]);

  const getFilterLabel = () => {
    const split = category.split(">") || [];
    if (split.length <= 0) {
      return "";
    }
    if (!split[1]) {
      return split[0];
    }
    return `${split[0]} > ${split[1]}`;
  };

  const submitAndCloseMenu = (callback: React.Dispatch<React.SetStateAction<boolean>>) => {
    // Seperate submit if neccessary : submitChanges()
    callback(false);
  };

  const components = [
    {
      isChipMenu: true,
      type: ComponentFilters.CATEGORY,
      label: "Category",
      testId: TestIds.components.filters.categoryChip,
      open: categoryOpen,
      setOpen: setCategoryOpen,
      value: getFilterLabel(),
      onClear: () => setCategory(CATEGORY_DEFAULT_OPTION),
      onSubmit: () => submitAndCloseMenu(setCategoryOpen),
      content: category ? (
        <DesktopCategoryMenu
          filters={{ categories }}
          selectedFilters={{
            category: { value: category, setValue: (v) => setCategory(v) },
          }}
          showSubCategories
        />
      ) : null,
    },
  ];

  return components.map((e) => {
    return (
      <ChipMenu
        key={e.label}
        {...e}
        dropdown={
          <FilterDropdown title={e.label} onClear={e.onClear} onSubmit={e.onSubmit}>
            {e.content}
          </FilterDropdown>
        }
      />
    );
  });
};

export default CategoriesDropDown;
